import Loader from "@components/Loader";
import ExtraValueAccordion, { extraValueActiveMutation, extraValueAppendMutation } from "@components/dashboard/extraValues/ExtraValueAccordion";
import ExtraValuesMonthlyChart from "@components/dashboard/extraValues/ExtraValuesMonthlyChart";
import ExtraValuesMonthlyTable from "@components/dashboard/extraValues/ExtraValuesMonthlyTable";
import ExtraValuesMonthDetailModal from "@components/dashboard/extraValues/ExtraValuesMonthDetailModal";
import { aggregateExtraValuesByMonth, extraValueMonths, toMonthLabel } from "@components/dashboard/extraValues/extraValuesMonthly";
import AiActionComposer from "@components/dashboard/ai/actions/AiActionComposer";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { formatNumberToCurrency, formatNumberToLocale } from "@utils/formatNumber";
import MoneyInput from "@utils/moneyInput";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";


const renderRow = (label, value, isActive) => {
  return <div className="extra-value-data">
    <span className="accordion-label">{label}</span>
    <span className="value" style={{ color: isActive ? '#333' : '#ccc' }}>{value}</span>
  </div>
}

const CreatedItem = ({ resource, onDocumentChange, highlight, readOnly }) => {
  const [newValue, setNewValue] = useState({
    value: 0,
    month: new Date().toISOString().slice(0, 7),
    installments: 1,
    type: 'positive',
    description: ''
  })
  const [historyVisible, setHistoryVisible] = useState(false)
  const [addVisible, setAddVisible] = useState(false)
  const [count, setCount] = useState(0)

  // Quando o usuário chega aqui pela tabela/modal mensal, abre o histórico.
  useEffect(() => {
    if (highlight) setHistoryVisible(true)
  }, [highlight])


  const { updateExtra } = useProjectActions();
  // formatNumberToCurrency chama useProjectContext por dentro; chamá-lo dentro
  // do histórico (renderizado condicionalmente) faz o nº de hooks variar quando
  // o histórico abre → "change in order of Hooks" e crash. Capturamos o contexto
  // uma vez aqui e formatamos com função pura.
  const { cubBased, activeProject: highlightProject } = useProjectContext()
  const indexerValue = highlightProject?.indexer?.value
  const fmtCurrency = (v) => {
    const safe = Number(v) || 0
    if (cubBased) return formatNumberToLocale(safe / (indexerValue && indexerValue !== 0 ? indexerValue : 1))
    return safe.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleSend = async () => {
    const firstMonth = newValue.month;
    const installments = parseInt(newValue.installments, 10) || 1;

    const [yearStr, monthStr] = String(firstMonth).split("-");
    const startYear = parseInt(yearStr, 10);
    const startMonth = parseInt(monthStr, 10);

    const totalMonths = startYear * 12 + (startMonth - 1) + (installments - 1);
    const lastYear = Math.floor(totalMonths / 12);
    const lastMonthNum = (totalMonths % 12) + 1;

    const pad = (n) => String(n).padStart(2, "0");
    const lastMonth = `${lastYear}-${pad(lastMonthNum)}`;

    const newEntry = {
      updated_at: new Date().toISOString(),
      value: Number(newValue.type === 'negative' ? -newValue.value : newValue.value),
      installments: newValue.installments,
      installment_value: Number(newValue.value / newValue.installments),
      start_date: firstMonth,
      end_date: lastMonth,
      isValid: true,
      description: newValue.description
    }
    const res = await updateExtra(resource.wbs_code_id, extraValueAppendMutation(newEntry), resource.item_name)
    if (res.code === 200 && res.document) {
      onDocumentChange?.(res.document)
    }
    setCount(count + 1)
  }

  const handleChange = (e) => {
    e.preventDefault()
    const { name, value } = e.target
    setNewValue((prevValue) => ({
      ...prevValue,
      [name]: value,
    }));
  }

  const handleValueChange = (e) => {
    setNewValue((prevValue) => ({
      ...prevValue,
      value: e,
    }));
  };

  const handleDisable = async (index) => {
    const entry = resource.history[index]
    const response = await updateExtra(
      resource.wbs_code_id,
      extraValueActiveMutation(entry, resource.history, index),
      resource.item_name,
    )
    if (response?.code === 200 && response.document) onDocumentChange?.(response.document)
    setCount(count + 1)
  }

  return <div id={`created-item-${resource.wbs_code_id}`}
    className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}
    style={highlight ? { outline: '2px solid #245AE4', outlineOffset: '2px', transition: 'outline .2s' } : undefined}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <span className="accordion-title" title={resource.item_name} >{resource.item_name}</span>
      </div>
    </div>
    <div className="grid gap-16 columns-5-laptop-l margin-top-12">
      <div className="data">
        <span className="accordion-label">Valor Total Atual</span>
        <span className="value">{fmtCurrency(resource?.history?.filter(i => i.isValid).reduce((a, b) => a + b.value, 0) || 0)}</span>
      </div>
      <div className="data">
        <span className="accordion-label">Número de Parcelas</span>
        <span className="value">{resource.history.filter(i => i.isValid).reduce((a, b) => a + b.installments, 0)}</span>
      </div>
    </div>
    <div>
      <div className="flex align-center margin-bottom-8 margin-top-8">
        <button
          className={`accordion-toggle ${historyVisible ? "active" : ""}`}
          onClick={() => setHistoryVisible(!historyVisible)}
        />
        <h2>Histórico</h2>
      </div>

      {historyVisible && <>
        {resource.history?.length > 0 ? resource.history.map((i, index) => {
          return <div key={i.entryId || `${i.start_date}-${i.end_date}-${i.value}-${i.description || ''}-${i.updated_at || ''}`} className="grid gap-16 columns-6-laptop-l margin-top-12 margin-left-12">
            {renderRow('Mes(es) referente(s)', `${i.installments > 1 ? `${i.start_date.split('-').reverse().join('/')} - ${i.end_date.split('-').reverse().join('/')}` : i.start_date.split('-').reverse().join('/')}`, i.isValid)}
            {renderRow('Valor', fmtCurrency(i.value || 0), i.isValid)}
            {renderRow('Parcelas', `${i.installments} (${fmtCurrency((i.value / i.installments) || 0)})`, i.isValid)}
            {renderRow('Descrição', i.description || '-', i.isValid)}
            {renderRow('Criado em', i.updated_at ? new Date(i.updated_at).toLocaleDateString() : '-', i.isValid)}
            {!readOnly && <button className={`button button-${i.isValid ? 'error' : 'green'} small`} style={{ justifySelf: 'end' }} onClick={() => handleDisable(index)}>{i.isValid ? 'Desativar' : 'Ativar'} valor</button>}
          </div>
        }) : <span>Nenhuma alteração registrada</span>}
      </>}


    </div>

    {!readOnly && <><div className="flex align-center margin-bottom-8">
      <button
        className={`accordion-toggle ${addVisible ? "active" : ""}`}
        onClick={() => setAddVisible(!addVisible)}
      />
      <h2>Adicionar Estouro / Economia</h2>
    </div>

    {addVisible && <div className="grid gap-16 align-center" style={{ gridTemplateColumns: '2fr 2fr 2fr 2fr 2fr 1fr' }}>
      <div>
        <label>Valor Total:</label>
        <MoneyInput value={newValue.value} onChange={handleValueChange} />
      </div>
      <div>
        <label>Tipo de valor:</label>
        <select onChange={(e) => setNewValue((prev) => ({ ...prev, type: e.target.value }))} value={newValue.type}>
          <option value="positive">
            Despesa
          </option>
          <option value="negative">
            Economia
          </option>
        </select>
      </div>
      <div>
        <label>Descrição:</label>
        <input
          style={{ marginBottom: '0px' }}
          type="text"
          name="description"
          value={newValue.description}
          onChange={handleChange}
          placeholder="Descrição"
        />
      </div>
      <div>
        <label>Mês:</label>
        <input
          style={{ marginBottom: '0px' }}
          type="month"
          name="month"
          value={newValue.month}
          onChange={handleChange}
          placeholder="Mês"
        />
      </div>
      <div>
        <label>Número de parcelas:</label>
        <select onChange={handleChange} name="installments" value={newValue.installments} style={{ marginBottom: '0px' }}>
          {Array.from({ length: 36 }, (_, i) => i + 1).map(num => (
            <option key={num} value={num}>{num} ({fmtCurrency(newValue.value / num)} por parcela)</option>
          ))}
        </select>
      </div>
      <button onClick={handleSend} className="button button-accent">Enviar</button>
    </div>}</>}
  </div>
}


const ExtraValues = () => {
  const { getAppropriation, getExtraValues, updateExtra, getAllBuildingItems } = useProjectActions();
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(false)
  const [allExtraValues, setAllExtraValues] = useState([])
  const [search, setSearch] = useState('')
  const [createdVisible, setCreatedVisible] = useState(true)
  const [monthlyTableVisible, setMonthlyTableVisible] = useState(true)
  const [monthDetail, setMonthDetail] = useState(null)
  const [highlightedItem, setHighlightedItem] = useState(null)
  const [monthFrom, setMonthFrom] = useState('')
  const [monthTo, setMonthTo] = useState('')
  const [openItems, setOpenItems] = useState({});
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')

  const toggleItem = (wbs_code_id) => {
    setOpenItems((prev) => ({
      ...prev,
      [wbs_code_id]: !prev[wbs_code_id],
    }));
  };

  const { activeProject } = useProjectContext()
  const readOnly = isPresentationProject(activeProject)
  const activeWorkIdRef = useRef(activeProject?._id)
  const requestGenerationRef = useRef(0)
  activeWorkIdRef.current = activeProject?._id

  const getItems = async (project) => {
    const generation = ++requestGenerationRef.current
    const workId = project?._id
    const buildingId = project?.building_id
    const isCurrentRequest = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    if (!workId || !buildingId) {
      if (isCurrentRequest()) {
        setResources([])
        setAllExtraValues([])
        setLoading(false)
      }
      return
    }
    setLoading(true)
    try {
      const response = await getAppropriation(project.appropriation_level)
      if (!isCurrentRequest()) return
      setResources([...(response?.uniqueDescs || [])].sort((a, b) => a.wbs_description.localeCompare(b.wbs_description)))
      const all = await getExtraValues(buildingId)
      if (!isCurrentRequest()) return
      setAllExtraValues(Array.isArray(all?.response) ? [...all.response] : [])
    } finally {
      if (isCurrentRequest()) setLoading(false)
    }
  }

  useEffect(() => {
    getItems(activeProject)
  }, [activeProject])

  const refreshExtraValues = async () => {
    const generation = ++requestGenerationRef.current
    const workId = activeProject?._id
    const buildingId = activeProject?.building_id
    if (!workId || !buildingId) return
    const isCurrentRefresh = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    try {
      const refreshed = await getExtraValues(buildingId)
      if (!isCurrentRefresh()) return
      setAllExtraValues(Array.isArray(refreshed?.response) ? [...refreshed.response] : [])
    } catch {
      // Estas outlets não exibem erro de carga; preserva os dados atuais.
    } finally {
      if (isCurrentRefresh()) setLoading(false)
    }
  }

  const getLevelItems = async () => {
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(res.items)
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()

    }
  }, [levelToGet])

  const updateAllCreated = (type, item) => {
    if (type === 'add') {
      setAllExtraValues(prev => [...prev, item])
    } else if (type === 'replace') {
      setAllExtraValues(prev => {
        const exists = prev.some(candidate => candidate._id === item._id || candidate.wbs_code_id === item.wbs_code_id)
        if (!exists) return [...prev, item]
        return prev.map(candidate => (
          candidate._id === item._id || candidate.wbs_code_id === item.wbs_code_id ? item : candidate
        ))
      })
    } else {
      setAllExtraValues(prev => prev.filter(i => i._id !== item._id))
    }
  }

  const monthly = useMemo(() => {
    const { months: allMonths } = extraValueMonths(allExtraValues)
    const from = monthFrom || allMonths[0]
    const to = monthTo || allMonths[allMonths.length - 1]
    return {
      ...aggregateExtraValuesByMonth(allExtraValues, from, to),
      allMonths,
      allMonthLabels: allMonths.map(toMonthLabel),
    }
  }, [allExtraValues, monthFrom, monthTo])

  // formatNumberToCurrency chama useProjectContext por dentro; precisa ser
  // chamado incondicionalmente (senão a ordem dos hooks muda quando os dados
  // carregam). Calcula o total dos "já criados" uma vez por render.
  const createdTotal = allExtraValues.reduce((a, b) => a + b.history.filter(j => j.isValid).reduce((x, y) => x + y.value, 0), 0)
  const createdTotalLabel = formatNumberToCurrency(createdTotal)

  // Vai do gráfico/tabela mensal para o lançamento de origem: abre a seção,
  // rola até o item e o destaca (o CreatedItem abre o histórico ao ser destacado).
  const goToItem = (wbs_code_id) => {
    setMonthDetail(null)
    setCreatedVisible(true)
    setHighlightedItem(wbs_code_id)
    setTimeout(() => {
      // behavior:'smooth' não rola nesta página (o container quebra o smooth);
      // 'auto' funciona.
      document.getElementById(`created-item-${wbs_code_id}`)?.scrollIntoView({ behavior: 'auto', block: 'center' })
    }, 60)
    setTimeout(() => setHighlightedItem(null), 2500)
  }

  return (
    <>
      {loading && <div className="margin-top-24"><Loader /></div>}
      <div className="resources-container">
        {!readOnly && activeProject?._id && <section className="margin-bottom-24" aria-label="Criar com IA">
          <h2>Criar com IA</h2>
          <AiActionComposer
            workId={activeProject._id}
            module="extra_value"
            onExecuted={refreshExtraValues}
          />
        </section>}
        {monthly.allMonths.length > 0 && <div className="margin-bottom-24">
          <hgroup className="dashboard-title flex wrap-wrap gap-16 align-center justify-content-space-between margin-bottom-8">
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Lançamentos por mês</span>
            <div className="flex gap-16 align-center wrap-wrap">
              <div className="flex gap-8 align-center">
                <span>De</span>
                <select style={{ marginBottom: 0, marginTop: 0 }} value={monthFrom || monthly.allMonths[0]} onChange={(e) => setMonthFrom(e.target.value)}>
                  {monthly.allMonths.map((m) => <option key={m} value={m}>{toMonthLabel(m)}</option>)}
                </select>
                <span>até</span>
                <select style={{ marginBottom: 0, marginTop: 0 }} value={monthTo || monthly.allMonths[monthly.allMonths.length - 1]} onChange={(e) => setMonthTo(e.target.value)}>
                  {monthly.allMonths.map((m) => <option key={m} value={m}>{toMonthLabel(m)}</option>)}
                </select>
                {(monthFrom || monthTo) && <button className="button button-secondary small" onClick={() => { setMonthFrom(''); setMonthTo('') }}>Limpar</button>}
              </div>
              <div className="flex gap-16 graph-labels">
                <h2 className={monthlyTableVisible ? 'active-graph' : ''} onClick={() => setMonthlyTableVisible(true)}>Tabela</h2>
                <h2 className={!monthlyTableVisible ? 'active-graph' : ''} onClick={() => setMonthlyTableVisible(false)}>Gráfico</h2>
              </div>
            </div>
          </hgroup>
          {monthly.error ? (
            <div className="alert alert-error" role="alert">
              <h3>Lançamentos por mês indisponíveis</h3>
              <p>{monthly.error.message}</p>
              <p>Selecione um período menor ou revise os lançamentos e tente novamente.</p>
            </div>
          ) : null}
          {!monthly.error && monthlyTableVisible ? <ExtraValuesMonthlyTable
              months={monthly.months}
              monthLabels={monthly.monthLabels}
              rows={monthly.rows}
              net={monthly.net}
              totals={monthly.totals}
              onSelectRow={goToItem}
            />
            : null}
          {!monthly.error && !monthlyTableVisible ? <ExtraValuesMonthlyChart
              months={monthly.months}
              monthLabels={monthly.monthLabels}
              expenses={monthly.expenses}
              savings={monthly.savings}
              onBarClick={setMonthDetail}
            />
            : null}
        </div>}
        {monthDetail && (
          <ExtraValuesMonthDetailModal
            monthLabel={toMonthLabel(monthDetail)}
            entries={monthly.entriesByMonth[monthDetail] || []}
            onSelectItem={goToItem}
            close={() => setMonthDetail(null)}
          />
        )}
        {allExtraValues?.length > 0 && <>
          <div className="flex align-center margin-bottom-8">
            <button
              className={`accordion-toggle ${createdVisible ? "active" : ""}`}
              onClick={() => setCreatedVisible(!createdVisible)}
            />
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Estouros / Economias {allExtraValues.length > 0 ? ` - ${createdTotalLabel}` : ''}</span>
          </div>
          {createdVisible && allExtraValues?.map((resource) => {
            return <CreatedItem key={resource._id || resource.wbs_code_id} resource={resource} onDocumentChange={(document) => updateAllCreated('replace', document)} highlight={highlightedItem === resource.wbs_code_id} readOnly={readOnly} />
          })}
        </>}
        {!readOnly && <>
        <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Todos itens</p>
        <div className="flex justify-content-center align-center gap-8">
          <span>Filtrar por:</span>
          <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setLevelToGet(e.target.value)
            setItemToFilter('')
          }} value={levelToGet}>
            <option value='' disabled>Selecione</option>
            <option value='units'>Unidade Construtiva</option>
            <option value='cells'>Célula</option>
            <option value='steps'>Etapa</option>
            <option value='substeps'>Subetapa</option>
            <option value='services'>Serviço</option>
          </select>
          {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => setItemToFilter(e.target.value)} value={itemToFilter}>
            <option value='' disabled>Selecione</option>
            {levelItems.map(i => {
              return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
            })}
          </select>}
          {itemToFilter !== '' && <button style={{ alignSelf: 'center' }} className="button button-secondary" onClick={() => setItemToFilter('')}>Limpar</button>}
        </div>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por item" />
        <div className="accordion">
          {resources?.length > 0 && (
            <>
              <div className="accordion">
                {resources.filter(i => i.wbs_code_id.includes(itemToFilter || '')).filter(i => i.wbs_description.toLowerCase().includes(search.toLowerCase())).map(i => {
                  return <ExtraValueAccordion
                    key={i.wbs_code_id}
                    item={i}
                    isOpen={!!openItems[i.wbs_code_id]}
                    toggleItem={toggleItem}
                    updateAllCreated={updateAllCreated}
                  />
                })}
              </div>
            </>
          )}
        </div>
        </>}
      </div>
    </>
  );
};

export default ExtraValues;
